import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ScheduleType } from '@octano/global-ui';
import { v4 as uuidv4 } from 'uuid';

import { SectionSchedule } from '../../../types/sectionMaintainerTypes';
import { SectionScheduleGetResponse } from '../types';
import { generateLessonDates, getDayIdByDate } from '../utils/index';
import { useGetDays } from './useGetDays';

export function useWeeklyScheduleCalendar(
  sectionSchedules: SectionSchedule[],
  section: SectionScheduleGetResponse['section'],
) {
  const { t } = useTranslation();

  // Estado para navegación semanal
  const [selectedWeek, setSelectedWeek] = useState({
    selectedDay: dayjs(),
    start: dayjs().startOf('w'),
    end: dayjs().endOf('w'),
  });

  // Headers de días con fechas específicas
  const days = useGetDays(selectedWeek);

  // Funciones de navegación
  const nextWeek = useCallback(() => {
    setSelectedWeek((prev) => ({
      selectedDay: prev.selectedDay.add(1, 'w'),
      start: prev.start.add(1, 'w'),
      end: prev.end.add(1, 'w'),
    }));
  }, []);

  const previousWeek = useCallback(() => {
    setSelectedWeek((prev) => ({
      selectedDay: prev.selectedDay.subtract(1, 'w'),
      start: prev.start.subtract(1, 'w'),
      end: prev.end.subtract(1, 'w'),
    }));
  }, []);

  const selectWeek = useCallback((date: string | Date | null) => {
    const selectedDate = dayjs(date || undefined);
    setSelectedWeek({
      selectedDay: selectedDate,
      start: selectedDate.startOf('w'),
      end: selectedDate.endOf('w'),
    });
  }, []);

  // Calcular todas las fechas que tienen clases (para DateInput)
  // Entre el año anterior y el siguiente al año actual (2025 - 2026 - 2027)
  const datesWithEvents = useMemo(() => {
    const allDates: Date[] = [];
    const yearStart = selectedWeek.selectedDay
      .startOf('year')
      .subtract(1, 'year');
    const yearEnd = selectedWeek.selectedDay.endOf('year').add(1, 'year');

    sectionSchedules.forEach((schedule) => {
      const dates = generateLessonDates(schedule);
      const datesInYear = dates.filter(
        (date) => !(date.isBefore(yearStart) || date.isAfter(yearEnd)),
      );
      datesInYear.forEach((date) => {
        allDates.push(date.toDate());
      });
    });

    return allDates;
  }, [sectionSchedules, selectedWeek.selectedDay]);

  // Generar instancias de clases para la semana seleccionada
  const schedules: ScheduleType[] = useMemo(() => {
    const lessonInstances: ScheduleType[] = [];

    sectionSchedules.forEach((schedule) => {
      // Generar todas las fechas para este horario
      const dates = generateLessonDates(schedule);

      // Filtrar solo las fechas que caen en la semana seleccionada
      const datesInWeek = dates.filter(
        (date) =>
          !(
            date.isBefore(selectedWeek.start) || date.isAfter(selectedWeek.end)
          ),
      );

      datesInWeek.forEach((date) => {
        lessonInstances.push({
          sectionId: section.id,
          scheduleId: uuidv4() as unknown, // ID único
          courseCode: section.course.name,
          sectionName: section.name,
          day: getDayIdByDate(date),
          startDate: date.format('YYYY-MM-DD'),
          endDate: date.format('YYYY-MM-DD'),
          activityId: schedule.activity.id,
          moduleStartId: schedule.startModule.id,
          moduleEndId: schedule.endModule?.id ?? 0,
          modality: schedule.modality?.deliveryMode,
          tooltip: {
            courseName: section.course.name,
            sectionTitle: t('common.scheduleTooltip.sectionTitle'),
            sectionName: section.name,
            teachers: [schedule.responsibles[0]?.account?.fullName ?? ''],
            teachersTitle: t('common.scheduleTooltip.teacherTitle'),
            classroom: schedule.classroom?.name ?? '',
            modality: schedule.modality?.deliveryMode,
          },
        } as ScheduleType);
      });
    });

    return lessonInstances;
  }, [section, sectionSchedules, selectedWeek, t]);

  return {
    selectedWeek,
    nextWeek,
    previousWeek,
    selectWeek,
    schedules,
    datesWithEvents,
    days,
  };
}
